import { SYNC_MESSENGER_MESSAGE_TYPES } from '../constants/messaging.constants';
import { SERVICE_WORKER_GENERAL_SETTINGS } from '../constants/service-worker.constants';
import { LogLevel } from '../constants/log.constants';

import { Logger } from './logger';
import { ServiceWorkerActivation } from './service-worker-activation';
import { SyncMessenger } from './sync-messenger';

export class ServiceWorkerControl{

  private logger: Logger;
  private serviceWorkerActivation: ServiceWorkerActivation;

  constructor(
    logLevel: LogLevel) {

    this.logger = new Logger(this.constructor.name, logLevel);
    this.serviceWorkerActivation = new ServiceWorkerActivation(logLevel);
  }

  // Returns a Promise that resolves with the active service worker after it has become 'activated' and after the service worker has claimed control.
  async wait(): Promise<ServiceWorker> {

    const logPrefix = `wait`;
    this.logger.log(`${logPrefix}: enter`);

    // 1. Wait for activation
    const serviceWorker = await this.serviceWorkerActivation.wait();

    // 2. If the Service Worker is active but not controlling, force the claim and wait.
    if (!navigator.serviceWorker.controller) {
      // This function will block until control is established or it times out.
      await this.claimClientAndAwaitControl(serviceWorker);
    }

    this.logger.log(`${logPrefix}: leave`);
    return serviceWorker;
  }

  private async claimClientAndAwaitControl(serviceWorker: ServiceWorker): Promise<void> {

    const logPrefix = `claimClientAndAwaitControl`;
    this.logger.log(`${logPrefix}: enter`);

    // 1. Create a promise that waits for the 'controllerchange' event.
    let handler: () => void = null;
    const controlChangePromise = new Promise<boolean>((resolve) => {
      handler = () => {
        if (navigator.serviceWorker.controller === serviceWorker) {
          resolve(true);
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', handler);
    });

    // 2. Send the claim request message asynchronously.
    this.logger.warn(`${logPrefix}: Sending '${SYNC_MESSENGER_MESSAGE_TYPES.clientClaimRequest}' message to Service Worker...`);
    SyncMessenger.postMessageNoWait(serviceWorker, SYNC_MESSENGER_MESSAGE_TYPES.clientClaimRequest);

    // 3. Wait for the Service Worker to fire the 'controllerchange' event.
    // We use a timeout as a safeguard, just in case the Service Worker fails to respond.
    const timeoutPromise = new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), SERVICE_WORKER_GENERAL_SETTINGS.ClientClaimRequestTimeOutMs);
    });

    try {
      const success = await Promise.race([controlChangePromise, timeoutPromise]);
      if (success) {
        this.logger.warn(`${logPrefix}: Control successfully reassigned after claim request`);
      }
      else {
        this.logger.error(`${logPrefix}: Timeout expired`);
      }
    }
    catch (e) {
      this.logger.error(`${logPrefix}: Error during control change race:`, e);
    }
    finally {
      if (handler) {
        navigator.serviceWorker.removeEventListener('controllerchange', handler);
      }
    }

    this.logger.log(`${logPrefix}: leave`);
  }
}
